// AuthContext.jsx - Authentication State Management
import React, { createContext, useContext, useState, useEffect } from 'react';
import * as authService from '../services/authService';
import * as profileService from '../services/profileService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ============================================
  // AUTHENTICATION METHODS
  // ============================================

  const signUp = async (email, password) => {
    try {
      setError(null);
      const result = await authService.signUpWithEmail(email, password);
      return result;
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  const signIn = async (email, password) => {
    try {
      setError(null);
      const result = await authService.signInWithEmail(email, password);
      return result;
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      setError(null);
      const result = await authService.signInWithGoogle();
      return result;
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  const signInWithFacebook = async () => {
    try {
      setError(null);
      const result = await authService.signInWithFacebook();
      return result;
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      setError(null);
      const result = await authService.resetPassword(email);
      return result;
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await authService.signOut();
      setCurrentUser(null);
      setUserProfile(null);
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  // ============================================
  // PROFILE METHODS
  // ============================================

  const loadProfile = async (userId) => {
    try {
      const profile = await profileService.getProfile(userId);
      setUserProfile(profile);
      return profile;
    } catch (error) {
      console.error('Error loading profile:', error);
      setUserProfile(null);
      return null;
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!currentUser) throw new Error('No user logged in');
      
      await profileService.updateProfile(currentUser.uid, updates);
      
      // Reload profile
      const updatedProfile = await loadProfile(currentUser.uid);
      return updatedProfile;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  const createProfile = async (profileData) => {
    try {
      if (!currentUser) throw new Error('No user logged in');
      
      await profileService.createProfile(currentUser.uid, profileData);
      
      // Load the new profile
      const newProfile = await loadProfile(currentUser.uid);
      return newProfile;
    } catch (error) {
      console.error('Error creating profile:', error);
      throw error;
    }
  };

  // ============================================
  // AUTH STATE LISTENER
  // ============================================

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange(async (user) => {
      console.log('👤 Auth state changed:', user?.email || 'No user');
      
      setCurrentUser(user);
      
      if (user) {
        // Load user profile
        await loadProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const value = {
    currentUser,
    userProfile,
    loading,
    error,
    
    // Auth methods
    signUp,
    signIn,
    signInWithGoogle,
    signInWithFacebook,
    resetPassword,
    signOut,
    
    // Profile methods
    loadProfile,
    updateProfile,
    createProfile,
    
    // Helper functions
    isProfileComplete: () => profileService.isProfileComplete(userProfile),
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
